import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Phone, Mail, Sparkles, Trophy, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  "Kulüp Başkanı",
  "Yönetim Kurulu Üyesi",
  "Medya Sorumlusu",
  "Teknik Direktör",
  "Antrenör",
  "Diğer",
];

const initial = {
  ad_soyad: "",
  kulup_adi: "",
  telefon: "",
  email: "",
  rol: "",
  mesaj: "",
};

export default function DemoRequestPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // capture source query param (?kaynak=hero / cta-bottom / pricing-starter etc.)
  const params = new URLSearchParams(location.search);
  const kaynak = params.get("kaynak") || "uye-ol-direct";

  useEffect(() => {
    // Title/meta handled by <MarketingSEO /> via react-helmet-async (set in App.js route).
    window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.ad_soyad.trim() || !form.kulup_adi.trim() || !form.telefon.trim()) {
      toast.error("Ad Soyad, Kulüp Adı ve Telefon zorunludur");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/public/leads`, {
        ad_soyad: form.ad_soyad.trim(),
        kulup_adi: form.kulup_adi.trim(),
        telefon: form.telefon.trim(),
        email: form.email.trim() || null,
        rol: form.rol || null,
        mesaj: form.mesaj.trim() || null,
        kaynak,
      });
      setDone(true);
      toast.success("Talebiniz alınmıştır. Sizlere en kısa sürede dönüş yapılacaktır.");
    } catch (err) {
      const msg = err.response?.data?.detail || "Bir hata oluştu, tekrar dener misin?";
      toast.error(typeof msg === "string" ? msg : "Bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-bg text-white flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-xl text-center"
          data-testid="demo-success"
        >
          <div className="inline-flex w-20 h-20 rounded-full bg-brand/15 border border-brand/40 items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-brand" />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black mb-4">
            Talebiniz <span className="text-brand">alınmıştır</span>
          </h1>
          <p className="text-white/70 text-lg leading-relaxed mb-8">
            Sizlere en kısa sürede dönüş yapılacaktır. Üyeliğiniz onaylandığında kulüp panelinizin
            giriş bilgileri <strong className="text-white">{form.telefon}</strong> numarasına iletilecektir.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              data-testid="demo-success-home"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-white/20 hover:border-white/40 transition font-semibold"
            >
              <ArrowLeft className="w-4 h-4" /> Ana Sayfaya Dön
            </Link>
            <a
              href="tel:+905437934101"
              data-testid="demo-success-call"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-brand text-black font-bold hover:opacity-90 transition"
            >
              <Phone className="w-4 h-4" /> Hemen Ara: 0543 793 41 01
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg text-white">
      {/* Mini nav */}
      <nav className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-lg" data-testid="demo-nav-home">
            DR AI <span className="text-brand">FUTBOL</span>
          </Link>
          <Link
            to="/giris"
            className="text-sm text-white/70 hover:text-brand transition"
            data-testid="demo-nav-login"
          >
            Giriş Yap →
          </Link>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-12 md:py-20 grid lg:grid-cols-5 gap-10 lg:gap-16">
        {/* Left: Pitch */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="lg:col-span-2"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand/10 border border-brand/30 text-brand text-xs font-label tracking-widest mb-6">
            <Sparkles className="w-3.5 h-3.5" /> ÜCRETSİZ ÜYELİK
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black leading-[1.05] mb-5">
            Ücretsiz üye ol, <span className="text-brand">takımını yönetmeye başla</span>
          </h1>
          <p className="text-white/70 text-base leading-relaxed mb-8">
            Kısa formu doldurun, kulüp hesabınızı biz açalım. Panel giriş bilgileriniz size iletilir;
            kadronuzu, fikstürünüzü ve kulüp kasanızı aynı gün yönetmeye başlayabilirsiniz.
          </p>

          <ul className="space-y-4 mb-10">
            {[
              { icon: Trophy, text: "Kadro, fikstür, puan durumu ve kulüp kasası tek panelde" },
              { icon: Sparkles, text: "Yapay zekâ ile hazır sosyal medya görselleri" },
              { icon: CheckCircle2, text: "Kulübünüze özel kurulum ve eğitim — kurulum ücreti yok" },
            ].map((it, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex w-8 h-8 rounded-lg bg-brand/10 border border-brand/30 items-center justify-center flex-shrink-0">
                  <it.icon className="w-4 h-4 text-brand" />
                </span>
                <span className="text-sm text-white/80 pt-1.5">{it.text}</span>
              </li>
            ))}
          </ul>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-xs font-label tracking-widest text-white/50 mb-3">DOĞRUDAN İLETİŞİM</p>
            <a
              href="tel:+905437934101"
              data-testid="demo-side-phone"
              className="flex items-center gap-3 text-white hover:text-brand transition mb-2"
            >
              <Phone className="w-4 h-4" /> <span className="font-semibold">0543 793 41 01</span>
            </a>
            <a
              href="mailto:info@dijitalroket.com"
              data-testid="demo-side-email"
              className="flex items-center gap-3 text-white/80 hover:text-brand transition text-sm"
            >
              <Mail className="w-4 h-4" /> info@dijitalroket.com
            </a>
          </div>
        </motion.div>

        {/* Right: Form */}
        <motion.form
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          onSubmit={onSubmit}
          data-testid="demo-form"
          className="lg:col-span-3 rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Ad Soyad *" testId="lead-ad-soyad">
              <input
                value={form.ad_soyad}
                onChange={set("ad_soyad")}
                placeholder="Mehmet Yıldız"
                required
                minLength={2}
                maxLength={120}
                className="form-input"
                data-testid="lead-ad-soyad-input"
              />
            </Field>
            <Field label="Kulüp Adı *" testId="lead-kulup">
              <input
                value={form.kulup_adi}
                onChange={set("kulup_adi")}
                placeholder="Özerspor"
                required
                minLength={2}
                maxLength={160}
                className="form-input"
                data-testid="lead-kulup-input"
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Telefon *" testId="lead-telefon">
              <input
                value={form.telefon}
                onChange={set("telefon")}
                placeholder="0543 793 41 01"
                required
                inputMode="tel"
                minLength={7}
                maxLength={30}
                className="form-input"
                data-testid="lead-telefon-input"
              />
            </Field>
            <Field label="E-posta (opsiyonel)" testId="lead-email">
              <input
                value={form.email}
                onChange={set("email")}
                type="email"
                placeholder="kulup@ornek.com"
                maxLength={160}
                className="form-input"
                data-testid="lead-email-input"
              />
            </Field>
          </div>

          <Field label="Kulüpteki Rolün" testId="lead-rol">
            <select
              value={form.rol}
              onChange={set("rol")}
              className="form-input"
              data-testid="lead-rol-input"
            >
              <option value="">Seçiniz…</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Mesajın (opsiyonel)" testId="lead-mesaj">
            <textarea
              value={form.mesaj}
              onChange={set("mesaj")}
              placeholder="Kulübümüz hakkında kısa bilgi, beklentilerin, öğrenmek istediklerin…"
              rows={4}
              maxLength={2000}
              className="form-input resize-none"
              data-testid="lead-mesaj-input"
            />
          </Field>

          <div className="text-xs text-white/40 leading-relaxed">
            Formu göndererek sizinle iletişime geçmemize izin vermiş olursunuz. Bilgileriniz
            yalnızca üyelik oluşturma amacıyla kullanılır.
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-5 py-3 rounded-lg border border-white/15 text-white/70 hover:border-white/30 transition text-sm font-semibold flex items-center justify-center gap-2"
              data-testid="lead-cancel"
            >
              <ArrowLeft className="w-4 h-4" /> Geri
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="lead-submit"
              className="flex-1 px-6 py-3 rounded-lg bg-brand text-black font-bold hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(227,255,0,0.25)]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Gönderiliyor…
                </>
              ) : (
                <>
                  Ücretsiz Üye Ol <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </motion.form>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          background: rgba(0,0,0,0.5);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 0.5rem;
          padding: 0.65rem 0.9rem;
          font-size: 0.875rem;
          color: white;
        }
        .form-input::placeholder { color: rgba(255,255,255,0.3); }
        .form-input:focus {
          outline: none;
          border-color: #E3FF00;
          box-shadow: 0 0 0 1px rgba(227,255,0,0.4);
        }
      `}</style>
    </div>
  );
}

const Field = ({ label, children, testId }) => (
  <div className="space-y-1.5" data-testid={testId}>
    <label className="text-xs font-label tracking-widest text-white/60 block uppercase">
      {label}
    </label>
    {children}
  </div>
);
